import { useEffect, useRef, useState } from 'react';
import { Check, Copy, Gem, Loader2 } from 'lucide-react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { getMe, getMyReferrals } from '@/services/authService';
import { writeClipboardText } from '@/utils/clipboard';
import { analyticsService } from '@/services/analyticsService';
import appIcon from '../../../../android/app/src/main/res/mipmap-xxxhdpi/ic_launcher.png';

interface NativeFilesystemPlugin { writeFile(options: { path: string; data: string; directory: string; recursive?: boolean }): Promise<{ uri: string }>; deleteFile(options: { path: string; directory: string }): Promise<void>; }
interface NativeSharePlugin { share(options: { title?: string; text?: string; files?: string[]; dialogTitle?: string }): Promise<void>; }
const Filesystem = registerPlugin<NativeFilesystemPlugin>('Filesystem');
const Share = registerPlugin<NativeSharePlugin>('Share');

const DISMISS_KEY = 'booka_referral_invite_dismissed';
const SHARE_COOLDOWN_KEY = 'booka_referral_invite_shared_at';
const SHARE_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const shownInSession = new Set<string>();
const wasDismissed = () => { try { return localStorage.getItem(DISMISS_KEY) === 'true'; } catch { return false; } };
const wasSharedRecently = () => {
  try {
    const sharedAt = Number(localStorage.getItem(SHARE_COOLDOWN_KEY) || 0);
    return sharedAt > 0 && Date.now() - sharedAt < SHARE_COOLDOWN_MS;
  } catch {
    return false;
  }
};
const toPersianDigits = (value: number | string) => String(value).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
const stageTitle = (n: number) => n === 1 ? 'نفر اول' : n === 2 ? 'نفر دوم' : n === 3 ? 'نفر سوم' : 'نفرات بعدی';

const ReferralInviteModal = ({ onClose }: { onClose: () => void }) => {
  const [data, setData] = useState<{ name: string; code: string; stages: Awaited<ReturnType<typeof getMyReferrals>>['reward_stages'] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);
  const wasReferralInviteVisible = useRef(false);

  useEffect(() => {
    const dismissed = wasDismissed();
    const sharedRecently = wasSharedRecently();
    const alreadyShownInSession = shownInSession.has('referral-invite');
    console.log('[ReferralInviteModal] Starting visibility check', { dismissed, sharedRecently, alreadyShownInSession, dismissValue: localStorage.getItem(DISMISS_KEY), sharedAt: localStorage.getItem(SHARE_COOLDOWN_KEY) });
    if (dismissed || sharedRecently || alreadyShownInSession) { onClose(); return; }
    console.log('[ReferralInviteModal] Requesting /auth/me and /auth/referrals/me');
    Promise.all([getMe(), getMyReferrals()]).then(([profile, referrals]) => {
      const userLevel = Number(profile.level);
      console.log('[ReferralInviteModal] API responses received', { profileLevel: profile.level, normalizedLevel: userLevel, referralCode: referrals.my_referral_code, referralCodeExists: Boolean(referrals.my_referral_code) });
      if (userLevel >= 4 && referrals.my_referral_code) {
        console.log('[ReferralInviteModal] Modal will be shown');
        setData({ name: profile.name || 'دوست من', code: referrals.my_referral_code, stages: referrals.reward_stages ?? [] });
      } else {
        console.warn('[ReferralInviteModal] Modal condition failed', { levelIsAtLeastFour: userLevel >= 4, hasReferralCode: Boolean(referrals.my_referral_code) });
        onClose();
      }
    }).catch((error) => { console.error('[ReferralInviteModal] API request failed', error); onClose(); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const isVisible = Boolean(data);
    if (isVisible && !wasReferralInviteVisible.current) {
      void analyticsService.logEvent('view_referral_invite', {
        referral_invite_source: 'learning_path',
      });
    }
    wasReferralInviteVisible.current = isVisible;
  }, [data]);

  const trackButtonClick = (buttonType: 'invite_friends' | 'later' | 'never_show_again') => {
    void analyticsService.logEvent('click_referral_invite', {
      referral_invite_source: 'learning_path',
      button_type: buttonType,
    });
  };

  const closeForSession = () => { shownInSession.add('referral-invite'); onClose(); };
  const handleLater = () => { trackButtonClick('later'); closeForSession(); };
  const dismiss = () => { trackButtonClick('never_show_again'); try { localStorage.setItem(DISMISS_KEY, 'true'); } catch {} closeForSession(); };
  const copyToClipboard = async (text: string) => { await writeClipboardText(text); };
  const handleCopyCode = async () => {
    if (!data?.code) return;
    try { await copyToClipboard(data.code); setCopied(true); window.setTimeout(() => setCopied(false), 1800); console.log('[ReferralInviteModal] Referral code copied successfully'); }
    catch (error) { console.error('[ReferralInviteModal] Failed to copy referral code', error); }
  };
  const share = async () => {
    if (!data || sharing) return;
    trackButtonClick('invite_friends');
    setSharing(true);
    const referralUrl = `${window.location.origin}/welcome`;
    const shareText = `با کد دعوت من ۳ روز اشتراک رایگان دریافت کن !\nکد دعوت : ${data.code}\n\n⬇️ دانلود از بازار : https://cafebazaar.ir/app/ir.booka.app\n⬇️ دانلود از مایکت : https://myket.ir/app/ir.booka.app`;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 420;
      canvas.height = 440;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas is not supported');
      ctx.fillStyle = '#7063FF';
      ctx.beginPath();
      ctx.roundRect(0, 0, 420, 440, 28);
      ctx.fill();
      const logo = new Image();
      logo.src = appIcon;
      await new Promise<void>((resolve) => { logo.onload = () => resolve(); logo.onerror = () => resolve(); });
      if (logo.complete && logo.naturalWidth > 0) ctx.drawImage(logo, 160, 28, 100, 100);
      ctx.direction = 'rtl';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#fff';
      ctx.font = '700 20px IRANSansX, sans-serif';
      ctx.fillText('با کد دعوت من ۳ روز اشتراک رایگان دریافت کن !', 210, 170);
      ctx.font = '700 22px IRANSansX, sans-serif';
      ctx.fillText('کد دعوت', 210, 235);
      ctx.font = '700 38px IRANSansX, sans-serif';
      ctx.fillText(data.code, 210, 295);
      ctx.font = '700 23px IRANSansX, sans-serif';
      ctx.fillText('بوکا؛ دنیای جدید کتاب‌ها', 210, 370);
      const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((result) => result ? resolve(result) : reject(new Error('Failed to create share image')), 'image/png'));
      const dataUrl = await new Promise<string>((resolve, reject) => { const reader = new FileReader(); reader.onload = () => resolve(String(reader.result || '')); reader.onerror = () => reject(new Error('Failed to read share image')); reader.readAsDataURL(blob); });

      if (Capacitor.isNativePlatform()) {
        const fileName = `booka-referral-${Date.now()}.png`;
        const file = await Filesystem.writeFile({ path: fileName, data: dataUrl.split(',')[1], directory: 'CACHE', recursive: true });
        try { await Share.share({ title: 'دعوت به بوکا', text: shareText, files: [file.uri], dialogTitle: 'اشتراک‌گذاری کد دعوت' }); }
        finally { await Filesystem.deleteFile({ path: fileName, directory: 'CACHE' }).catch(() => {}); }
      } else {
        const nav = navigator as Navigator & { canShare?: (value: ShareData) => boolean; share?: (value?: ShareData) => Promise<void> };
        const shareFile = new File([blob], 'booka-referral.png', { type: 'image/png' });
        if (typeof nav.share === 'function') {
          if (!nav.canShare || nav.canShare({ files: [shareFile] })) await nav.share({ title: 'دعوت به بوکا', text: shareText, files: [shareFile] });
          else await nav.share({ title: 'دعوت به بوکا', text: shareText, url: referralUrl });
        } else await copyToClipboard(shareText);
      }
      try {
        localStorage.setItem(SHARE_COOLDOWN_KEY, String(Date.now()));
        console.log('[ReferralInviteModal] Seven-day share cooldown saved');
      } catch (error) {
        console.error('[ReferralInviteModal] Failed to save share cooldown', error);
      }
      closeForSession();
    } finally { setSharing(false); }
  };
  if (loading || !data) return null;
  const achievedCount = data.stages.filter((stage) => stage.achieved).length;
  const currentStageNumber = data.stages.length ? (data.stages.find((stage) => !stage.achieved)?.stage_number ?? data.stages[data.stages.length - 1].stage_number) : 1;

  return <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 px-4" dir="rtl">
    <div className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl bg-[#FFFDFC] px-6 pb-5 pt-1 shadow-2xl" role="dialog" aria-modal="true">
      <img src="/referal_icon.webp" alt="" className="mx-auto h-48 w-48 object-contain" />
      <h2 className="text-center text-base font-bold text-gray-900">{data.name} دوستاتو دعوت کن به بوکا !</h2>
      <p className="mt-2 text-center text-xs leading-6 text-gray-700">با کد دعوت <strong>{data.code}</strong> دوستاتو به بوکا دعوت کن و الماس بگیر 🔥</p>
      <div className="mt-3 rounded-3xl border border-[#B9F1DD] bg-[#E9FBF4] p-3"><div className="flex items-center justify-between gap-3"><div><div className="font-persian text-[11px] text-muted">کد دعوت شما</div><div className="mt-1 font-mono text-xl font-semibold tracking-[0.2em] text-foreground">{data.code}</div></div><button type="button" onClick={() => void handleCopyCode()} aria-label="کپی کد دعوت" className="flex h-10 w-10 items-center justify-center rounded-2xl border border-[#B9F1DD] bg-white text-[#239E76] shadow-sm"><Copy className="h-4 w-4" /></button></div>{copied && <p className="mt-1 font-persian text-[11px] text-emerald-700">کد کپی شد.</p>}</div>
      <section className="mt-3 rounded-[28px] border border-gray-100 bg-white/90 p-3 shadow-sm backdrop-blur-sm"><div className="mb-3 flex items-center justify-between"><div><h2 className="font-persian text-base font-bold text-foreground">مراحل پاداش</h2><p className="mt-1 font-persian text-[11px] text-muted">{toPersianDigits(achievedCount)} از {toPersianDigits(data.stages.length)} مرحله تکمیل شده</p></div><div className="rounded-full bg-[#E9FBF4] px-2.5 py-1 font-persian text-[10px] font-medium text-[#239E76]">مرحله فعلی: {toPersianDigits(currentStageNumber)}</div></div><div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.max(data.stages.length, 1)}, minmax(0, 1fr))` }}>{data.stages.map((stage) => <div key={stage.stage_number} className="flex flex-col items-center"><div className="mb-2 flex min-h-[40px] flex-col items-center justify-end text-center"><div className="flex items-center gap-1 font-persian text-[10px] font-semibold text-foreground"><Gem className={`h-3 w-3 ${stage.achieved ? 'text-[#35D6A0]' : 'text-gray-300'}`} /><span>{toPersianDigits(stage.gems)}</span></div><div className="mt-0.5 flex items-center gap-1 font-persian text-[9px] text-muted"><span>{stageTitle(stage.stage_number)}</span>{stage.achieved && <Check className="h-3 w-3 text-[#35D6A0]" />}</div></div><div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${stage.achieved ? 'bg-[#35D6A0]' : 'bg-transparent'}`} style={{ width: stage.achieved ? '100%' : '0%' }} /></div></div>)}</div></section>
      <button type="button" onClick={() => void share()} disabled={sharing} className="mt-4 w-full rounded-2xl bg-[#35D6A0] px-4 py-3 text-sm font-bold text-white disabled:opacity-60">{sharing ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : 'دعوت دوستان'}</button>
      <div className="mt-2 flex gap-3"><button type="button" onClick={handleLater} className="flex-[1.5] rounded-xl border border-gray-300 bg-gray-300 py-2 text-sm font-bold text-gray-800">بعدا</button><button type="button" onClick={dismiss} className="flex-1 rounded-xl border border-gray-400 py-2 text-sm font-bold text-gray-700">دیگه نمایش نده</button></div>
    </div>
  </div>;
};
export default ReferralInviteModal;
